import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { WebRootState } from "@/website/store/web-store";
import { CommonRootState } from '@common/types/common-root-state-type.d';
import { ArrowUpRight, AlertCircle, ListChecks, Loader2 } from "lucide-react";
import React from 'react';
import { useSelector } from 'react-redux';
import {
  selectOverviewData,
  selectOverviewState,
  selectNodeEventStatsData
} from '../BotUsageDashboardSelectors';

/**
 * OverviewStats Component
 * 
 * Displays snapshot stats of the bot usage dashboard in a grid of cards
 */
export const OverviewStats: React.FC = () => {
  // Get real data from Redux store
  const overviewState = useSelector((state: WebRootState) => selectOverviewState(state));
  const stats = useSelector((state: WebRootState) => selectOverviewData(state));
  const nodeEventStats = useSelector((state: WebRootState) => selectNodeEventStatsData(state));
  
  // Check loading state for both overview and node events
  const isLoading = overviewState.loading || !stats || !nodeEventStats;
  
  // Return loading state if data is not yet available
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  // Actual data values from API
  const totalAgents = stats.totalAgents || 0;
  const deployedAgents = stats.deployedAgents || 0;
  const draftAgents = stats.draftAgents || 0;
  const activeAgents = stats.activeAgents || 0;
  const totalEvents = nodeEventStats.totalEvents || 0;
  const totalErrors = nodeEventStats.totalErrors || 0;
  
  // Calculated percentages (with safety checks)
  const deployedPercentage = totalAgents > 0 ? Math.round((deployedAgents / totalAgents) * 100) : 0;
  const draftPercentage = totalAgents > 0 ? Math.round((draftAgents / totalAgents) * 100) : 0;
  const activePercentage = deployedAgents > 0 ? Math.round((activeAgents / deployedAgents) * 100) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">Total Agents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{totalAgents}</div>
          {stats.botUsageGrowth !== undefined && (
            <div className="flex items-center mt-1 text-sm">
              <span className={`flex items-center ${stats.botUsageGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.botUsageGrowth >= 0 ? '+' : ''}{stats.botUsageGrowth}%
                <ArrowUpRight className="h-3 w-3 ml-1" />
              </span>
              <span className="text-slate-500 ml-2">growth</span>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">Deployed vs Draft</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{deployedAgents} / {draftAgents}</div>
          <div className="mt-2">
            <Progress value={deployedPercentage} className="h-2" />
          </div>
          <div className="flex justify-between mt-1 text-xs text-slate-500">
            <span>Deployed ({deployedPercentage}%)</span>
            <span>Draft ({draftPercentage}%)</span>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">Active Agents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{activeAgents}</div>
          {activeAgents > 0 && (
            <>
              <div className="mt-2">
                <Progress value={activePercentage} className="h-2" />
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {activePercentage}% of deployed agents with recent activity
              </div>
            </>
          )}
          {activeAgents === 0 && (
            <div className="mt-1 text-xs text-slate-500">
              No active agents detected
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-500">Avg. Nodes Per Agent</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{stats.averageNodesPerAgent?.toFixed(1) || '0'}</div>
          <div className="mt-1 text-xs text-slate-500">
            Average number of nodes in agent workflow
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium text-slate-500">Total Events</CardTitle>
          <ListChecks className="h-4 w-4 text-slate-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{totalEvents.toLocaleString()}</div>
          <div className="mt-1 text-xs text-slate-500">
            All recorded node events
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium text-slate-500">Total Errors</CardTitle>
          <AlertCircle className="h-4 w-4 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{totalErrors.toLocaleString()}</div>
          <div className="mt-1 text-xs text-slate-500">
            Node events with 'ERROR' status
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 